/* eslint-disable react-hooks/exhaustive-deps */
import { useCreatePensionAccount } from 'common/queries-and-mutations/pension';
import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import { InfoModal } from 'components/Modal';
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { PensionAccountType } from 'apps/user/types/on-boarding.type';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

export default function AddPensionModal({ onClose, onSuccess }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isError, isLoading } = useCreatePensionAccount();

  const formik = useFormik({
    initialValues: {
      pensionId: null,
      pensionAdministratorName: '',
      contributorName: '',
      rsaAddress: '',
      pinNumber: ''
    },
    validationSchema: Yup.object({
      spouse: Yup.array().of(
        Yup.object().shape({
          pensionAdministratorName: Yup.string().required(
            'Pension Fund Administrator is required'
          ),
          contributorName: Yup.string()
            .required('Contributor name is required')
            .nullable(),
          // pinNumber: Yup.string().required('PIN number is required').nullable(),
          rsaAddress: Yup.string()
            .required('RSA Address is required')
            .nullable()
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onPensionSubmit(values);
    }
  });

  const onPensionSubmit = (formValue: PensionAccountType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      toast(<Alert message="RSA Account Successfully Added" type="success" />);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <InfoModal width="w-[60%]" className="h-fit rounded-md">
      <div className="flex flex-row items-center justify-between border-b p-3 pl-6">
        <p className="text-2xl font-bold text-[#353535]">Add RSA Account</p>
        <button type="button" onClick={onClose}>
          <Icon name={Icons.Close} />
        </button>
      </div>
      <form autoComplete="false" className="p-8" onSubmit={formik.handleSubmit}>
        <div className="my-8">
          <div className="grid md:grid-cols-2 md:gap-6">
            <div className="group relative z-0 mb-6 w-full">
              <TextField
                name="pensionAdministratorName"
                placeholder=""
                value={formik.values.pensionAdministratorName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Name of Pension Fund Administrator"
                error={
                  formik.touched.pensionAdministratorName
                    ? formik.errors.pensionAdministratorName
                    : ''
                }
                isRequired
                size="lg"
                className="rounded-2xl border-[1.5px] border-gray-300"
              />
            </div>
            <div className="group relative z-0 mb-6 w-full">
              <TextField
                name="contributorName"
                placeholder=""
                value={formik.values.contributorName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Name of Contributor"
                error={
                  formik.touched.contributorName
                    ? formik.errors.contributorName
                    : ''
                }
                isRequired
                size="lg"
                className="rounded-2xl border-[1.5px] border-gray-300"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 md:gap-6">
            <div className="group relative z-0 mb-6 w-full">
              <TextField
                name="rsaAddress"
                placeholder=""
                value={formik.values.rsaAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Address of RSA"
                error={
                  formik.touched.rsaAddress ? formik.errors.rsaAddress : ''
                }
                size="lg"
                className="rounded-2xl border-[1.5px] border-gray-300"
                isRequired
              />
            </div>
            <div className="group relative z-0 mb-6 w-full">
              <TextField
                name="pinNumber"
                placeholder=""
                value={formik.values.pinNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="PIN no."
                error={formik.touched.pinNumber ? formik.errors.pinNumber : ''}
                size="lg"
                className="rounded-2xl border-[1.5px] border-gray-300"
              />
            </div>
          </div>
        </div>
        <div className="mt-10 flex items-end justify-end gap-8">
          <Button
            variant="primary"
            size="custom"
            className="mt-5 rounded p-3"
            label="Add new RSA Account"
            type="submit"
            disabled={!formik.isValid}
            loading={isSubmitting || isLoading}
          ></Button>
        </div>
      </form>
    </InfoModal>
  );
}
